<template>
  <div class="mod-admin">
    <div class="page-header">
      <span class="page-header__title">管理员</span>
    </div>

    <div class="page-body">
      <div class="toolbar">
        <el-input
          v-model="dataForm.username"
          placeholder="账号"
          clearable
          class="toolbar__search"
          @keyup.enter.native="handleSearch">
        </el-input>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <div class="toolbar__right">
          <el-button type="primary" icon="el-icon-plus" @click="addOrUpdate()">新增管理员</el-button>
        </div>
      </div>

      <!-- 管理员列表 -->
      <div class="panel">
        <el-table :data="dataList" v-loading="loading" style="width: 100%;">
          <el-table-column prop="username" label="账号" min-width="140"></el-table-column>
          <el-table-column label="姓名" min-width="140">
            <template slot-scope="scope">{{ scope.row.realName || '—' }}</template>
          </el-table-column>
          <el-table-column label="角色" min-width="200">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.superAdmin === 1" type="danger" size="small" class="role-tag">超级管理员</el-tag>
              <el-tag
                v-for="name in scope.row.roleNames"
                :key="name"
                size="small"
                class="role-tag">
                {{ name }}
              </el-tag>
              <span v-if="scope.row.superAdmin !== 1 && !scope.row.roleNames.length">—</span>
            </template>
          </el-table-column>
          <el-table-column label="最后登录" min-width="170">
            <template slot-scope="scope">{{ scope.row.lastLogin || '—' }}</template>
          </el-table-column>
          <el-table-column prop="createDate" label="创建时间" min-width="170"></el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === userStatus.ENABLED ? 'success' : 'danger'" size="small">
                {{ scope.row.status === userStatus.ENABLED ? '正常' : '停用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="150" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="addOrUpdate(scope.row.id)">编辑</el-button>
              <!-- 超级管理员不允许禁用 -->
              <el-button
                v-if="scope.row.superAdmin !== 1"
                type="text"
                size="small"
                :class="scope.row.status === userStatus.ENABLED ? 'danger-text' : ''"
                @click="handleToggle(scope.row)">
                {{ scope.row.status === userStatus.ENABLED ? '禁用' : '启用' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          :current-page="page"
          :page-sizes="[10, 20, 50]"
          :page-size="limit"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          class="pagination"
          @size-change="handleSizeChange"
          @current-change="handlePageChange">
        </el-pagination>
      </div>

      <!-- 角色权限说明 -->
      <div class="panel panel--role">
        <div class="panel__header">
          <span>角色权限说明</span>
          <span class="panel__tip">权限范围取自角色备注，可在「角色管理」中维护</span>
        </div>
        <el-table :data="roleList" v-loading="roleLoading" style="width: 100%;">
          <el-table-column prop="name" label="角色" min-width="180"></el-table-column>
          <el-table-column label="权限范围" min-width="360">
            <template slot-scope="scope">{{ scope.row.remark || '—' }}</template>
          </el-table-column>
          <el-table-column prop="createDate" label="创建时间" min-width="170"></el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 弹窗：新增 / 修改管理员（复用系统用户模块） -->
    <add-or-update v-if="dialogVisible" ref="addOrUpdate" @refreshDataList="loadList"></add-or-update>
  </div>
</template>

<script>
import AddOrUpdate from '../sys/user-add-or-update'
import {
  getSysUserPage,
  getSysUserInfo,
  updateSysUser,
  getRolePage,
  getLoginLogPage,
  USER_STATUS
} from '@/api/sysManage'

// 用于推算最后登录时间的登录日志条数
const LOGIN_LOG_LIMIT = 300

export default {
  name: 'AdminManage',
  components: {
    AddOrUpdate
  },
  data () {
    return {
      loading: false,
      roleLoading: false,
      dataList: [],
      roleList: [],
      page: 1,
      limit: 10,
      total: 0,
      dataForm: {
        username: ''
      },
      // 角色ID -> 角色名
      roleMap: {},
      // 账号 -> 最后登录时间
      lastLoginMap: {},
      dialogVisible: false
    }
  },

  computed: {
    userStatus () {
      return USER_STATUS
    }
  },

  async created () {
    await Promise.all([this.loadRoles(), this.loadLastLogin()])
    this.loadList()
  },

  methods: {
    loadRoles () {
      this.roleLoading = true
      return getRolePage({ page: 1, limit: 100 }).then(({ data: res }) => {
        if (res.code !== 0) {
          return this.$message.error(res.msg)
        }
        this.roleList = (res.data && res.data.list) || []
        const map = {}
        this.roleList.forEach(item => {
          map[item.id] = item.name
        })
        this.roleMap = map
      }).catch(() => {
        this.$message.error('加载角色失败')
      }).finally(() => {
        this.roleLoading = false
      })
    },

    // 管理员表没有最后登录字段，从登录日志里取每人最近一条
    loadLastLogin () {
      return getLoginLogPage({ page: 1, limit: LOGIN_LOG_LIMIT }).then(({ data: res }) => {
        if (res.code !== 0) {
          return
        }
        const list = (res.data && res.data.list) || []
        const map = {}
        list.forEach(item => {
          const name = item.creatorName
          if (!name) {
            return
          }
          // 同一账号保留时间最新的一条
          if (!map[name] || String(item.createDate) > String(map[name])) {
            map[name] = item.createDate
          }
        })
        this.lastLoginMap = map
      }).catch(() => {})
    },

    async loadList () {
      this.loading = true
      try {
        const { data: res } = await getSysUserPage({
          page: this.page,
          limit: this.limit,
          username: this.dataForm.username || undefined
        })
        if (res.code !== 0) {
          this.$message.error(res.msg)
          return
        }
        const list = (res.data && res.data.list) || []
        this.total = (res.data && res.data.total) || 0

        // 列表接口通常不返回 roleIdList，缺失时逐个补详情
        const detailed = await Promise.all(list.map(item => {
          if (item.roleIdList) {
            return Promise.resolve(item)
          }
          return getSysUserInfo(item.id)
            .then(({ data: detail }) => (detail.code === 0 ? { ...item, ...detail.data } : item))
            .catch(() => item)
        }))

        this.dataList = detailed.map(item => ({
          ...item,
          roleNames: (item.roleIdList || []).map(id => this.roleMap[id]).filter(Boolean),
          // 接口若已返回 lastLoginTime 就直接用，否则回退到登录日志的推算值
          lastLogin: item.lastLoginTime || item.lastLogin || this.lastLoginMap[item.username] || ''
        }))
      } catch (e) {
        console.error('加载管理员失败:', e)
        this.$message.error('加载管理员失败')
      } finally {
        this.loading = false
      }
    },

    handleSearch () {
      this.page = 1
      this.loadList()
    },

    handleSizeChange (val) {
      this.limit = val
      this.page = 1
      this.loadList()
    },

    handlePageChange (val) {
      this.page = val
      this.loadList()
    },

    addOrUpdate (id) {
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.addOrUpdate.dataForm.id = id || ''
        this.$refs.addOrUpdate.init()
      })
    },

    // 取完整信息后只改状态回写；不带 password，避免触发改密
    handleToggle (row) {
      const enabled = row.status === USER_STATUS.ENABLED
      const nextStatus = enabled ? USER_STATUS.DISABLED : USER_STATUS.ENABLED
      this.$confirm(`确定${enabled ? '禁用' : '启用'}管理员「${row.username}」？`, '提示', {
        type: 'warning'
      }).then(() => {
        return getSysUserInfo(row.id)
      }).then(({ data: res }) => {
        if (res.code !== 0) {
          return Promise.reject(new Error(res.msg || '获取管理员信息失败'))
        }
        const payload = { ...res.data, status: nextStatus }
        delete payload.password
        delete payload.confirmPassword
        return updateSysUser(payload)
      }).then(({ data: res }) => {
        if (res.code !== 0) {
          return Promise.reject(new Error(res.msg || '操作失败'))
        }
        this.$message.success(enabled ? '已禁用' : '已启用')
        this.loadList()
      }).catch(e => {
        if (e && e.message) {
          this.$message.error(e.message)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.mod-admin {
  background-color: #f5f7fa;
  min-height: 100%;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: #fff;
  border-bottom: 1px solid #ebeef5;

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2d3d;
  }
}

.page-body {
  padding: 16px;
}

.toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  &__search {
    width: 220px;
    margin-right: 12px;
  }

  &__right {
    margin-left: auto;
  }
}

.panel {
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 0 16px 16px;
  margin-bottom: 20px;

  &--role {
    padding-top: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    padding: 16px 0;
    font-size: 14px;
    font-weight: 600;
    color: #1f2d3d;
  }

  &__tip {
    margin-left: 10px;
    font-size: 12px;
    font-weight: 400;
    color: #909399;
  }
}

.role-tag {
  margin-right: 6px;
}

.danger-text {
  color: #f56c6c;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

::v-deep .el-table th {
  background-color: #fafbfc;
  color: #909399;
  font-weight: 500;
}
</style>
